import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BB_REVOKE_MAX_IDS,
  BB_REVOKE_REASON_MAX,
  BbRevokeDistributionsResult,
  bbRevokeManualDistributions,
} from '../../../services/blindBoxService';
import { decimalToNum, shortfallOf, summarizeRevoke, validateRevoke } from './revokeHelpers';

const fmt = (n: number): string => n.toLocaleString(undefined, { maximumFractionDigits: 6 });

/**
 * Revoke dialog for one or more manual distributions. Step 1 asks for a reason (with a clear
 * irreversible-action warning); step 2 shows the per-record result. `onDone` fires when the dialog
 * is closed after a request went through, so the caller can reload its list and stats.
 */
export function RevokeDialog({
  open,
  ids,
  retry,
  onClose,
  onDone,
}: {
  open: boolean;
  ids: string[];
  /** All targets are already revoked and only the clawback shortfall is being collected. */
  retry?: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BbRevokeDistributionsResult | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setResult(null);
    }
  }, [open]);

  const submit = async () => {
    const err = validateRevoke(ids, reason);
    if (err) {
      notify(t(err, { max: BB_REVOKE_MAX_IDS, maxLen: BB_REVOKE_REASON_MAX }), {
        type: 'warning',
      });
      return;
    }
    setBusy(true);
    try {
      const res = await bbRevokeManualDistributions({
        distributionIds: ids,
        reason: reason.trim(),
      });
      setResult(res);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    if (result) onDone();
    onClose();
  };

  const summary = result ? summarizeRevoke(result) : null;

  return (
    <Dialog open={open} onClose={close} maxWidth={result ? 'md' : 'sm'} fullWidth>
      <DialogTitle>
        {t(retry ? 'module.blindBox.mdRetryClawbackTitle' : 'module.blindBox.mdRevokeTitle', {
          count: ids.length,
        })}
      </DialogTitle>
      <DialogContent>
        {!result ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="error" variant="outlined">
              {t('module.blindBox.mdRevokeWarning', { count: ids.length })}
            </Alert>
            {ids.length > BB_REVOKE_MAX_IDS ? (
              <Alert severity="warning">
                {t('module.blindBox.mdRevokeErrTooMany', { max: BB_REVOKE_MAX_IDS })}
              </Alert>
            ) : null}
            <TextField
              label={t('module.blindBox.mdRevokeReason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              fullWidth
              multiline
              minRows={2}
              size="small"
              inputProps={{ maxLength: BB_REVOKE_REASON_MAX }}
              helperText={`${reason.length}/${BB_REVOKE_REASON_MAX}`}
            />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={result.failureCount > 0 ? 'warning' : 'success'}>
              {t('module.blindBox.mdRevokeSummary', {
                success: result.successCount,
                failure: result.failureCount,
                boxes: summary?.boxesRevoked ?? 0,
                amount: fmt(summary?.clawedBackNow ?? 0),
              })}
            </Alert>
            {summary && summary.remainingShortfall > 0 ? (
              <Alert severity="warning">
                {t('module.blindBox.mdRevokeShortfallHint', {
                  amount: fmt(summary.remainingShortfall),
                })}
              </Alert>
            ) : null}
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.blindBox.mdColId')}</TableCell>
                    <TableCell>{t('module.blindBox.mdColStatus')}</TableCell>
                    <TableCell align="right">{t('module.blindBox.mdRevokeColBoxes')}</TableCell>
                    <TableCell align="right">{t('module.blindBox.mdRevokeColClawedNow')}</TableCell>
                    <TableCell align="right">{t('module.blindBox.mdColShortfall')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.results.map((row) => (
                    <TableRow key={row.distributionId}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.distributionId}
                      </TableCell>
                      <TableCell>
                        {row.success ? (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={t(
                              row.alreadyRevoked
                                ? 'module.blindBox.mdRevokeAlready'
                                : 'module.blindBox.mdRevokeRowOk'
                            )}
                          />
                        ) : (
                          <Box>
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label={row.errorCode || t('module.blindBox.mdRevokeRowFailed')}
                            />
                            {row.errorMessage ? (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                {row.errorMessage}
                              </Typography>
                            ) : null}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">{row.boxesRevoked ?? 0}</TableCell>
                      <TableCell align="right">{fmt(decimalToNum(row.clawedBackNow))}</TableCell>
                      <TableCell align="right">
                        {row.record ? fmt(shortfallOf(row.record)) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button onClick={close} disabled={busy}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={submit}
              disabled={busy || !reason.trim() || ids.length > BB_REVOKE_MAX_IDS}
            >
              {t(retry ? 'module.blindBox.mdRetryClawback' : 'module.blindBox.mdRevokeConfirm')}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={close}>
            {t('module.blindBox.mdRevokeClose')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
